import cookies from 'js-cookie';
import { AccountType } from '@/common/constants';
import { toThousands } from '@/utils/money';
import { translation } from '@/plugins/i18n';

export const ZOOM_CSRF_TOKEN = 'ZOOM-CSRFTOKEN';

/**
 *
 * @param {*} url
 * @param {*} method
 * @param {*} data
 */
export const formJump = (url, method = 'POST', data = null) => {
  const form = document.createElement('form');
  form.action = url;
  form.method = method;
  form.id = 'jump_form';
  form.class = 'hidden_element';
  if (data) {
    for (let key in data) {
      if (data[key] === undefined) {
        continue;
      }
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = data[key];
      form.appendChild(input);
    }

    const input = document.getElementsByName(ZOOM_CSRF_TOKEN);
    if (input.length !== 0) {
      form.appendChild(input[0]);
    }
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

export const $cookie = cookies.withConverter({
  write: function (value) {
      return value;
  },
  read: cookies.converter.read,
});

export const isPaidAccount = (accountType) => {
  // exclude net new/free w/wo cc
  return [
    AccountType.ACCOUNT_UNKNOWN,
    AccountType.ACCOUNT_FREE_WITH_CC,
    AccountType.ACCOUNT_FREE_WITHOUT_CC
  ].includes(accountType) === false;
}

export const capitalizeFirstLetter = (string = '') => {
  if (typeof string !== 'string') return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const capacityFormatter = (capacity) => {
  const unitMap = {
    1000000: translation('billing.event_services.unit.million_1_uppercase')
  };
  return unitMap?.[capacity] || toThousands(capacity);
}

export const toLowercaseString = (str) => {
  return str?.toLowerCase?.() ?? str;
}

export const toUpCaseString = (str) => {
  return str?.toUpperCase?.() ?? str;
}

export const stripHTML = (htmlStr) => {
  const div = document.createElement('div');
  div.innerHTML = htmlStr;
  return div.textContent || div.innerText || '';
}

export const deduplicate = arr => [...new Set(arr)];

export const getAddonLearnMoreMap = () => {
  return {
    zoom_scheduler: { dialogName: 'zsched' },
    large_meeting: { dialogName: 'large' },
    cmr: { dialogName: 'cmr' },
    translation_captions: { dialogName: 'translation_captions' },
    webinar: { dialogName: 'webinar' },
    zwbbasic: { dialogName: 'whiteboard', hideOptions: true },
    zwbplus: {
      dialogName: 'zwbplus',
      hideOptions: true,
      title: translation('billing.planform.title.zoom_whiteboard_plus'),
    },
    tollfree: { dialogName: 'tollfree' },
    pbx$zpa: { dialogName: 'powerPack' },
    pbx$zpi: { dialogName: 'internationalCalling' },
    pbx$additional: { dialogName: 'additionalPhoneNumbers' },
    pro: { dialogName: 'pro', hideOptions: true },
    zoneproplus: { dialogName: 'zoneproplus', hideOptions: true, title: translation('billing.zone_pro_plus.title') },
    biz: { dialogName: 'biz', hideOptions: true, title: translation('billing.planform.title.zoom_one_biz') },
    zonebundle: {
      dialogName: 'zone_bizplus',
      hideOptions: true,
      title: translation('billing.planform.title.zoom_one_biz_plus'),
    },
    zoomroom: { dialogName: 'zr' },
    rc: { dialogName: 'rc' },
    eventsaascore: { dialogName: 'eventsaascore' },
    custom_avatars: { dialogName: 'custom_avatar' },
    workspace_reservation: { dialogName: 'workspace_reservation' },
    zvstmgt: { dialogName: 'zvstmgt' },
    zoom_enhanced_media: { dialogName: 'enhanced_media' },
    // Don't have learn more dialog now.
    // zoom_email_calendar: { dialogName: 'zoom_email_calendar' },
  };
};
/**
 * Check whether a date is expired.
 *
 * expireDate is treated as a UTC date in the format YYYY-MM-DD.
 * The date is considered valid until the end of that UTC day
 * (23:59:59.999Z).
 *
 * @param {string} expireDate - Expiration date in YYYY-MM-DD format (UTC)
 * @param {Date} [now=new Date()] - Current time (injectable for testing)
 * @returns {boolean} - True if expired, false otherwise
 */
export function isExpired(expireDate, now = new Date()) {
  if (!expireDate) return true;

  // End of the expiration day in UTC: 23:59:59.999Z
  const expireEndUTC = new Date(`${expireDate}T23:59:59.999Z`);

  if (Number.isNaN(expireEndUTC.getTime())) {
    throw new TypeError('Invalid expireDate format, expected YYYY-MM-DD');
  }

  return now.getTime() > expireEndUTC.getTime();
}

/**
 * Pick a minimal set of types that "hits" every dependency group.
 *
 * Each dependency group must contain at least one selected type.
 * This function uses a greedy approximation of the
 * Minimum Hitting Set problem, which is sufficient for most
 * engineering scenarios (plugins, addons, capability resolution).
 *
 * @param {string[][]} depsList
 *   A list of dependency groups.
 *   Example: [['A', 'B'], ['B', 'C'], ['C', 'D']]
 *
 * @returns {string[]}
 *   A minimal set of types that covers all dependency groups.
 */
export function pickMinimalTypes(depsList) {
  // Remaining dependency groups that are not covered yet
  const remaining = depsList.map(deps => new Set(deps))

  // Final selected types
  const result = new Set()

  while (remaining.length > 0) {
    const count = new Map()

    // Count how many dependency groups each type can cover
    for (const deps of remaining) {
      for (const type of deps) {
        count.set(type, (count.get(type) || 0) + 1)
      }
    }

    // Select the type that covers the most remaining groups
    let bestType = null
    let maxCoverage = 0

    for (const [type, coverage] of count) {
      if (coverage > maxCoverage) {
        maxCoverage = coverage
        bestType = type
      }
    }

    // Add the selected type to the result set
    result.add(bestType)

    // Remove all dependency groups covered by this type
    for (let i = remaining.length - 1; i >= 0; i--) {
      if (remaining[i].has(bestType)) {
        remaining.splice(i, 1)
      }
    }
  }

  return Array.from(result)
}
